<template>
  <div
    class="grid gap-4 p-4"
    v-loading="loading"
    element-loading-background="rgba(24, 33, 48, 0.8)"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2 class="text-lg font-semibold leading-none tracking-tight">
        {{ title }}
      </h2>
      <p class="text-sm text-muted-foreground">Update recipient information</p>
    </div>
    <el-form class="flex flex-col">
      <div class="flex flex-col">
        <el-form-item label="Full Name" prop="name" label-position="top">
          <el-input
            id="edit-recipient-name"
            placeholder="e.g., John Smith"
            v-model="recipient.name"
          />
        </el-form-item>
      </div>
      <fieldset class="flex flex-col border rounded p-4 gap-4">
        <legend class="text-sm font-medium leading-none">
          Notification Channels
        </legend>
        <div class="flex flex-row gap-4 items-center">
          <SmartSelect
            v-model="selectChannel"
            placeholder="Select Channel Type"
            class="w-120"
            :options="enumToOptions(NotificationRecipientChannelTypes)"
          />
          <el-button
            type="primary"
            plain
            icon="el-icon-plus"
            @click="addChannel"
          >
            Add Channel
          </el-button>
        </div>
        <span>Channels:</span>
        <div v-if="recipient.channels.length" class="space-y-2">
          <component
            v-for="(channel, idx) in recipient.channels"
            :key="channel.recipient_channel_id"
            :is="getChannelComponent(channel.channel)"
            v-model="recipient.channels[idx]"
            @remove="removeChannel(idx)"
          />
        </div>
        <el-empty v-else class="text-sm text-muted-foreground">
          Click the
          <el-button
            type="primary"
            plain
            icon="el-icon-plus"
            @click="addChannel"
          >
            Add Channel
          </el-button>
          button to add notification channels for this recipient.
        </el-empty>
      </fieldset>
    </el-form>
    <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
      <el-button @click="$emit('cancel')"> Cancel </el-button>
      <el-button :loading="submitting" type="primary" @click="handleSubmit">
        {{ id == null ? "Add Recipient" : "Save Changes" }}
      </el-button>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <i class="icon-x h-4 w-4" />
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>

<script>
import {
  createNotificationRecipientV2,
  getNotificationRecipientV2,
  updateNotificationRecipientV2,
  NotificationRecipientChannelTypes,
} from "@/api";
import EditRecipientWhatsAppItem from "./EditRecipientWhatsAppItem.vue";
import EditRecipientWhatsAppGroupItem from "./EditRecipientWhatsAppGroupItem.vue";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import EditRecipientWhatsAppEmailItem from "./EditRecipientWhatsAppEmailItem.vue";
import EditRecipientWhatsAppVoiceCallItem from "./EditRecipientWhatsAppVoiceCallItem.vue";
import { enumToOptions } from "@/enums";

const channelComponentMap = {
  [NotificationRecipientChannelTypes.WHATS_APP]: {
    compoennt: EditRecipientWhatsAppItem,
    model: { config: { phone: "" } },
  },
  [NotificationRecipientChannelTypes.WHATS_APP_GROUP]: {
    compoennt: EditRecipientWhatsAppGroupItem,
    model: { config: { group_id: "" } },
  },
  [NotificationRecipientChannelTypes.EMAIL]: {
    compoennt: EditRecipientWhatsAppEmailItem,
    model: { config: { email: "" } },
  },
  [NotificationRecipientChannelTypes.VOICE_CALL]: {
    compoennt: EditRecipientWhatsAppVoiceCallItem,
    model: { config: { phone: "" } },
  },
};

export default {
  name: "EditRecipient",
  components: {
    EditRecipientWhatsAppItem,
    EditRecipientWhatsAppGroupItem,
    EditRecipientWhatsAppEmailItem,
    EditRecipientWhatsAppVoiceCallItem,
    SmartSelect,
  },
  props: {
    id: {
      type: [String, null],
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      recipient: {
        name: "",
        channels: [],
      },
      selectChannel: NotificationRecipientChannelTypes.WHATS_APP,
      loading: false,
      submitting: false,
    };
  },
  computed: {
    NotificationRecipientChannelTypes: () => NotificationRecipientChannelTypes,
  },
  methods: {
    enumToOptions,
    addChannel() {
      return channelComponentMap[this.selectChannel]
        ? this.recipient.channels.push({
            channel: this.selectChannel,
            config: channelComponentMap[this.selectChannel].model.config,
          })
        : null;
    },
    removeChannel(idx) {
      this.recipient.channels.splice(idx, 1);
    },
    getChannelComponent(channelType) {
      return channelComponentMap[channelType]
        ? channelComponentMap[channelType].compoennt
        : null;
    },
    async loadRecipientData() {
      this.loading = true;
      try {
        const response = await getNotificationRecipientV2(String(this.id));
        this.recipient = response;
        if (!this.recipient.channels || this.recipient.channels.length === 0) {
          this.recipient.channels = [
            {
              channel: NotificationRecipientChannelTypes.WHATS_APP,
              config: { phone: "" },
            },
          ];
        }
      } catch {
        this.$message.error("Failed to load recipient data.");
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      this.submitting = true;
      try {
        if (this.id) {
          await updateNotificationRecipientV2(this.recipient);
          this.$message.success("Recipient updated successfully.");
        } else {
          await createNotificationRecipientV2(this.recipient);
          this.$message.success("Recipient created successfully.");
        }
        this.$emit("confirm");
      } catch {
        this.$message.error("Failed to save recipient.");
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    if (this.id) {
      this.loadRecipientData();
    }
  },
};
</script>
